import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Descriptions, Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Popconfirm, notification, Typography, Space, Tag, Spin, Card, Alert, Checkbox } from 'antd';
import { SendOutlined, EditOutlined, StopOutlined, PrinterOutlined, CheckCircleOutlined, ThunderboltOutlined, CloseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { LETTER_STATUS_COLORS } from './EngagementLettersPage';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { PdfButton, EmailButton } from '../../components/DocumentActions';

const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SECTIONS = [
  ['scope_summary', 'Scope of services'],
  ['billing_arrangements', 'Billing arrangements'],
  ['firm_responsibilities', 'Our responsibilities'],
  ['client_responsibilities', 'Your responsibilities'],
  ['disclaimers', 'Limitations and termination'],
];

/**
 * One Engagement Letter: the document as the client sees it, plus the
 * lifecycle actions. Staff edit the draft, send it, record a signature that
 * came back by e-signature / on paper, cancel, and activate services once
 * signed; a client portal user reads their SENT letter and signs or declines
 * it here (spec §9 "signed through the client portal").
 */
export default function EngagementLetterDetailPage() {
  const { id } = useParams();
  const auth = useAuth();
  const isClientUser = auth.user.role === 'CLIENT_ADMIN';
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const [letter, setLetter] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [modal, setModal] = React.useState(null); // 'edit' | 'send' | 'sign' | 'portal-sign' | 'decline'
  const [form] = Form.useForm();

  const fetchLetter = () => {
    setLoading(true);
    request
      .get(`/engagement-letters/${id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setLetter(r.data);
        else notification.error({ message: r.message });
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchLetter, [id]);

  const apply = (path, body, successMessage) => {
    setBusy(true);
    return request
      .post(`/engagement-letters/${id}/${path}`, body || {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: typeof successMessage === 'function' ? successMessage(r.data) : successMessage });
          setLetter(r.data);
          setModal(null);
        } else {
          notification.error({ message: r.message });
        }
      })
      .finally(() => setBusy(false));
  };

  const openEdit = () => {
    form.resetFields();
    form.setFieldsValue({
      title: letter.title,
      issue_date: moment(letter.issue_date),
      effective_date: moment(letter.effective_date),
      valid_until: letter.valid_until ? moment(letter.valid_until) : null,
      scope_summary: letter.scope_summary,
      billing_arrangements: letter.billing_arrangements,
      firm_responsibilities: letter.firm_responsibilities,
      client_responsibilities: letter.client_responsibilities,
      disclaimers: letter.disclaimers,
      notes: letter.notes,
      lines: letter.EngagementLetterServices.map((l) => ({
        service_id: l.service_id,
        proposal_id: l.proposal_id,
        description: l.description,
        fee_basis: l.fee_basis,
        annual_fee: l.annual_fee === null ? null : Number(l.annual_fee),
        frequency: l.frequency,
      })),
    });
    setModal('edit');
  };

  const handleEditSave = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        issue_date: values.issue_date.format('YYYY-MM-DD'),
        effective_date: values.effective_date.format('YYYY-MM-DD'),
        valid_until: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : null,
      };
      setBusy(true);
      request
        .put(`/engagement-letters/${id}`, payload)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Draft updated' });
            setLetter(r.data);
            setModal(null);
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setBusy(false));
    });
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (modal === 'send') apply('send', values, 'Letter sent to the client');
      if (modal === 'sign') apply('sign', { ...values, signed_at: values.signed_at ? values.signed_at.toISOString() : null }, (l) => `Signed - ${(l.kycRequestsRaised || []).length} KYC document request(s) raised`);
      if (modal === 'portal-sign') apply('sign', { signed_by_name: values.signed_by_name }, 'Thank you - the engagement letter is signed');
      if (modal === 'decline') apply('decline', values, 'Letter declined');
    });
  };

  if (loading || !letter) return <Spin style={{ marginTop: 40 }} />;

  const status = letter.status;
  const pendingActivation = status === 'SIGNED' && letter.EngagementLetterServices.some((l) => !l.engagement_id);

  return (
    <div>
      <style>{`@media print { .ant-layout-sider, .ant-layout-header, .ant-layout-footer, .no-print { display: none !important; } .ant-layout-content { margin: 0 !important; } }`}</style>

      <Space className="no-print" style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {letter.letter_number} <Tag color={LETTER_STATUS_COLORS[status]}>{status}</Tag>
          </Typography.Title>
          <Typography.Text type="secondary">
            <Link to="/engagement-letters">Engagement Letters</Link> / {letter.Company?.name}
          </Typography.Text>
        </div>
        <Space wrap>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
            Print
          </Button>
          <PdfButton type="engagement-letter" id={+id} />
          {!isClientUser && ['DRAFT', 'SENT', 'SIGNED'].includes(status) && (
            <EmailButton type="engagement-letter" id={+id} onSent={fetchLetter}>
              {status === 'DRAFT' ? 'E-mail to Client' : 'E-mail'}
            </EmailButton>
          )}
          {!isClientUser && status === 'DRAFT' && (
            <>
              <Button icon={<EditOutlined />} onClick={openEdit}>
                Edit Draft
              </Button>
              <Button type="primary" icon={<SendOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ sent_via: 'EMAIL', sent_to: letter.Client?.contact_email }); setModal('send'); }}>
                Send to Client
              </Button>
            </>
          )}
          {!isClientUser && (status === 'DRAFT' || status === 'SENT') && (
            <Button icon={<CheckCircleOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ signature_method: 'EMAIL_DIGITAL', signed_at: moment(), signed_by_name: letter.Client?.contact_person }); setModal('sign'); }}>
              Record Signature
            </Button>
          )}
          {isClientUser && status === 'SENT' && (
            <>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ signed_by_name: auth.user.name }); setModal('portal-sign'); }}>
                Sign Engagement Letter
              </Button>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => { form.resetFields(); setModal('decline'); }}>
                Decline
              </Button>
            </>
          )}
          {!isClientUser && status === 'SENT' && (
            <Button icon={<CloseCircleOutlined />} onClick={() => { form.resetFields(); setModal('decline'); }}>
              Mark Declined
            </Button>
          )}
          {pendingActivation && !isClientUser && (
            <Button type="primary" icon={<ThunderboltOutlined />} loading={busy} onClick={() => apply('activate-services', {}, (l) => `${(l.engagementsCreated || []).length} service engagement(s) created`)}>
              Activate Services
            </Button>
          )}
          {isManager && (status === 'DRAFT' || status === 'SENT') && (
            <Popconfirm title="Cancel this engagement letter?" onConfirm={() => apply('cancel', {}, 'Letter cancelled')}>
              <Button danger icon={<StopOutlined />} loading={busy}>
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>

      {status === 'SUPERSEDED' && <Alert className="no-print" type="warning" showIcon style={{ marginBottom: 16 }} message="This letter has been superseded by a later signed engagement letter." />}
      {status === 'SIGNED' && !pendingActivation && letter.EngagementLetterServices.length > 0 && (
        <Alert className="no-print" type="success" showIcon style={{ marginBottom: 16 }} message="Signed and all services activated." />
      )}

      {/* ---- The document ---- */}
      <Card style={{ maxWidth: 900 }}>
        <Typography.Title level={5} style={{ marginBottom: 0 }}>{letter.Company?.name}</Typography.Title>
        <Typography.Text type="secondary">
          {[letter.Company?.address, letter.Company?.registration_number && `Reg. ${letter.Company.registration_number}`, letter.Company?.tax_id && `VAT ${letter.Company.tax_id}`].filter(Boolean).join(' - ')}
        </Typography.Text>

        <Descriptions size="small" column={2} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Letter">{letter.letter_number}</Descriptions.Item>
          <Descriptions.Item label="Date">{letter.issue_date}</Descriptions.Item>
          <Descriptions.Item label="To">
            {letter.Client ? <Link to={`/clients/${letter.Client.id}`}>{letter.Client.client_name}</Link> : '-'}
            {letter.Client?.contact_person ? ` (attn. ${letter.Client.contact_person})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Effective from">{letter.effective_date}</Descriptions.Item>
          <Descriptions.Item label="Engaged entity">
            {letter.Entity ? `${letter.Entity.entity_name}${letter.Entity.registration_number ? ` (Reg. ${letter.Entity.registration_number})` : ''}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Offer valid until">{letter.valid_until || '-'}</Descriptions.Item>
        </Descriptions>

        <Typography.Title level={4} style={{ marginTop: 16 }}>{letter.title}</Typography.Title>
        <Typography.Paragraph>
          Dear {letter.Client?.contact_person || 'Sir/Madam'}, this letter sets out the basis on which {letter.Company?.name} will act for{' '}
          {letter.Entity?.entity_name || letter.Client?.client_name} and the respective responsibilities of both parties.
        </Typography.Paragraph>

        <Typography.Title level={5}>Services and fees</Typography.Title>
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={letter.EngagementLetterServices}
          columns={[
            { title: '#', dataIndex: 'sort_order', width: 40 },
            { title: 'Service', dataIndex: 'description' },
            { title: 'Fee basis', dataIndex: 'fee_basis' },
            { title: 'Annual fee', dataIndex: 'annual_fee', align: 'right', render: (v) => (v === null ? 'Time-spent' : money(v)) },
            { title: 'Billing', dataIndex: 'frequency', render: (v) => v.replace('_', '-').toLowerCase() },
            ...(isClientUser ? [] : [{ title: 'Engagement', key: 'eng', className: 'no-print', render: (_, l) => (l.ClientServiceEngagement ? <Tag color="green">#{l.ClientServiceEngagement.id} {l.ClientServiceEngagement.status}</Tag> : <Tag>not activated</Tag>) }]),
          ]}
        />

        {SECTIONS.map(([key, heading]) =>
          letter[key] ? (
            <div key={key} style={{ marginTop: 16 }}>
              <Typography.Title level={5}>{heading}</Typography.Title>
              <Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>{letter[key]}</Typography.Paragraph>
            </div>
          ) : null
        )}

        <Descriptions title="Acceptance" size="small" column={2} bordered style={{ marginTop: 24 }}>
          <Descriptions.Item label="Sent">{letter.sent_at ? `${moment(letter.sent_at).format('YYYY-MM-DD HH:mm')} via ${letter.sent_via}${letter.sent_to ? ` to ${letter.sent_to}` : ''}` : 'Not yet sent'}</Descriptions.Item>
          <Descriptions.Item label="Prepared by">{letter.CreatedBy?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Signed by">{letter.signed_by_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Signed at">{letter.signed_at ? moment(letter.signed_at).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Method">{letter.signature_method ? letter.signature_method.replace('_', ' ') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Reference">{[letter.signature_reference, letter.signature_ip && `IP ${letter.signature_ip}`].filter(Boolean).join(' - ') || '-'}</Descriptions.Item>
          {letter.declined_at && (
            <Descriptions.Item label="Declined" span={2}>
              {moment(letter.declined_at).format('YYYY-MM-DD')} {letter.declined_reason ? `- ${letter.declined_reason}` : ''}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title="Files" size="small" className="no-print" style={{ maxWidth: 900, marginTop: 16 }}>
        <Typography.Paragraph type="secondary">
          Upload the signed copy as "Signed copy" once it comes back; it is kept with the letter as evidence of acceptance.
        </Typography.Paragraph>
        <AttachmentsPanel ownerType="ENGAGEMENT_LETTER" ownerId={+id} categories={['SIGNED_COPY', 'CORRESPONDENCE', 'GENERAL']} canDelete={!isClientUser} onChange={fetchLetter} />
      </Card>

      {/* ---- Modals ---- */}
      <Modal title="Edit Draft" open={modal === 'edit'} onCancel={() => setModal(null)} onOk={handleEditSave} confirmLoading={busy} width={900}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Space>
            <Form.Item name="issue_date" label="Issue Date" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="effective_date" label="Effective From" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="valid_until" label="Offer Valid Until">
              <DatePicker />
            </Form.Item>
          </Space>
          <Typography.Title level={5}>Services</Typography.Title>
          <Form.List name="lines">
            {(fields) =>
              fields.map((field) => (
                <Space key={field.key} align="start" style={{ display: 'flex' }}>
                  <Form.Item {...field} name={[field.name, 'service_id']} hidden><InputNumber /></Form.Item>
                  <Form.Item {...field} name={[field.name, 'proposal_id']} hidden><InputNumber /></Form.Item>
                  <Form.Item {...field} name={[field.name, 'description']} rules={[{ required: true }]}>
                    <Input placeholder="Description" style={{ width: 300 }} />
                  </Form.Item>
                  <Form.Item {...field} name={[field.name, 'fee_basis']}>
                    <Select style={{ width: 110 }} options={['FIXED', 'HOURLY'].map((v) => ({ value: v, label: v }))} />
                  </Form.Item>
                  <Form.Item {...field} name={[field.name, 'annual_fee']}>
                    <InputNumber min={0} placeholder="Annual fee" style={{ width: 130 }} />
                  </Form.Item>
                  <Form.Item {...field} name={[field.name, 'frequency']}>
                    <Select style={{ width: 130 }} options={['ONE_OFF', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'].map((v) => ({ value: v, label: v }))} />
                  </Form.Item>
                </Space>
              ))
            }
          </Form.List>
          {SECTIONS.map(([key, heading]) => (
            <Form.Item key={key} name={key} label={heading}>
              <Input.TextArea rows={key === 'scope_summary' ? 3 : 4} />
            </Form.Item>
          ))}
          <Form.Item name="notes" label="Internal notes (not printed)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Send to Client" open={modal === 'send'} onCancel={() => setModal(null)} onOk={handleModalOk} confirmLoading={busy}>
        <Form form={form} layout="vertical">
          <Form.Item name="sent_via" label="Channel" rules={[{ required: true }]}>
            <Select options={[{ value: 'PORTAL', label: 'Client portal' }, { value: 'EMAIL', label: 'E-mail' }, { value: 'MOBILE', label: 'Mobile app' }]} />
          </Form.Item>
          <Form.Item name="sent_to" label="Sent to (e-mail / portal user)">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Record Client Signature" open={modal === 'sign'} onCancel={() => setModal(null)} onOk={handleModalOk} confirmLoading={busy}>
        <Typography.Paragraph type="secondary">
          Use this when the signed letter came back outside the portal. Signing supersedes any earlier signed letter for this entity and raises the KYC
          document requests.
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="signed_by_name" label="Signed by (name)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="signature_method" label="Method" rules={[{ required: true }]}>
            <Select options={[{ value: 'EMAIL_DIGITAL', label: 'E-signature by e-mail' }, { value: 'MOBILE', label: 'Mobile app' }, { value: 'WET_INK', label: 'Signed on paper' }]} />
          </Form.Item>
          <Form.Item name="signed_at" label="Signed at" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="signature_reference" label="Reference (envelope id / scan ref)">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Sign Engagement Letter" open={modal === 'portal-sign'} onCancel={() => setModal(null)} onOk={handleModalOk} confirmLoading={busy} okText="I agree and sign">
        <Form form={form} layout="vertical">
          <Typography.Paragraph>
            By signing you confirm that you have read this engagement letter and accept its terms on behalf of {letter.Entity?.entity_name || letter.Client?.client_name}.
            Your name, the time and your network address are recorded with the signature.
          </Typography.Paragraph>
          <Form.Item name="signed_by_name" label="Your full name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="agree" valuePropName="checked" rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error('Please confirm'))) }]}>
            <Checkbox>I have read and accept the terms of this engagement letter</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Decline Engagement Letter" open={modal === 'decline'} onCancel={() => setModal(null)} onOk={handleModalOk} confirmLoading={busy}>
        <Form form={form} layout="vertical">
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
